import { useCallback } from 'react';
import { useMutation, useQueryClient, type InfiniteData } from '@tanstack/react-query';
import * as chatApi from '../../api/chat';
import type { ChatMessage, EditMessageRequest, SendMessageRequest } from '../../types/chat';
import { chatKeys } from './chatKeys';
import { useCurrentChatIdentity } from './useCurrentChatIdentity';

// Helper that mutates the InfiniteData<ChatMessage[]> cache for a room.
// We always operate on shallow copies to keep TanStack's reference equality
// invariants intact.
function patchMessagesCache(
  qc: ReturnType<typeof useQueryClient>,
  roomId: string,
  patch: (pages: ChatMessage[][]) => ChatMessage[][]
): void {
  qc.setQueryData<InfiniteData<ChatMessage[]>>(chatKeys.messages(roomId), (prev) => {
    if (!prev) return prev;
    return { ...prev, pages: patch(prev.pages) };
  });
}

function genOptimisticId(): string {
  // Synthetic ULID-shaped id that sorts after real ones for the moment.
  // We replace it with the real id once the server echoes the message back.
  const rand = Math.random().toString(36).slice(2, 18).toUpperCase().padEnd(16, 'X');
  return `01${'Z'.repeat(8)}${rand}`;
}

export function useSendMessage(roomId: string) {
  const qc = useQueryClient();
  const { userId } = useCurrentChatIdentity();

  const mutation = useMutation({
    mutationFn: async (vars: { req: SendMessageRequest; optimisticId: string }) => {
      return chatApi.sendMessage(roomId, vars.req);
    },
    onMutate: async (vars) => {
      const optimistic: ChatMessage = {
        id: vars.optimisticId,
        room_id: roomId,
        sender_id: userId ?? '',
        body: vars.req.body ?? null,
        kind: 'text',
        attachment_ids: vars.req.attachment_ids ?? [],
        edited_at: null,
        deleted_at: null,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        pending: true,
      };
      patchMessagesCache(qc, roomId, (pages) => {
        if (pages.length === 0) return [[optimistic]];
        const head = pages[0];
        // Pages are DESC; prepend the optimistic to the first page.
        return [[optimistic, ...head], ...pages.slice(1)];
      });
    },
    onSuccess: (real, vars) => {
      patchMessagesCache(qc, roomId, (pages) =>
        pages.map((page) =>
          page.map((m) => (m.id === vars.optimisticId ? real : m))
        )
      );
      if (real.attachment_ids.length > 0) {
        qc.invalidateQueries({ queryKey: chatKeys.attachments(roomId) });
      }
    },
    onError: (err, vars) => {
      patchMessagesCache(qc, roomId, (pages) =>
        pages.map((page) =>
          page.map((m) =>
            m.id === vars.optimisticId
              ? { ...m, pending: false, send_error: (err as Error).message ?? 'Send failed' }
              : m
          )
        )
      );
    },
  });

  const send = useCallback(
    (req: SendMessageRequest) => {
      const optimisticId = genOptimisticId();
      mutation.mutate({ req, optimisticId });
      return optimisticId;
    },
    [mutation]
  );

  return { ...mutation, send };
}

export function useEditMessage(roomId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { messageId: string; req: EditMessageRequest }) =>
      chatApi.editMessage(roomId, vars.messageId, vars.req),
    onSuccess: (msg) => {
      patchMessagesCache(qc, roomId, (pages) =>
        pages.map((page) => page.map((m) => (m.id === msg.id ? msg : m)))
      );
    },
  });
}

export function useDeleteMessage(roomId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (messageId: string) => chatApi.deleteMessage(roomId, messageId),
    onSuccess: (_void, messageId) => {
      patchMessagesCache(qc, roomId, (pages) =>
        pages.map((page) =>
          page.map((m) =>
            m.id === messageId
              ? { ...m, body: null, deleted_at: new Date().toISOString() }
              : m
          )
        )
      );
    },
  });
}

export function useMarkRead(roomId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (messageId?: string) =>
      chatApi.markRead(roomId, messageId ? { message_id: messageId } : {}),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: chatKeys.unread() });
      qc.invalidateQueries({ queryKey: ['chat', 'rooms'] });
    },
  });
}
